import React, { useState, useRef, useEffect } from 'react';
import { Palette, Plus, Pencil, Trash2, Save, X, RefreshCw, GripVertical, ChevronLeft, ChevronRight, Eye, Search, List, Table, Download, SlidersHorizontal } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

/**
 * StyleGuidePage - Living reference for UI patterns and components.
 * Interactive examples that demonstrate the standard patterns.
 * TODO: Delete this file before production deployment
 */

const TABS = [
  { id: 'headers', label: 'Headers' },
  { id: 'crud', label: 'CRUD & Buttons' },
  { id: 'module', label: 'Dynamic Module' },
  { id: 'forms', label: 'Forms & Layout' },
  { id: 'grids', label: 'Data Grids' },
  { id: 'colors', label: 'Text & Colors' },
  { id: 'reporting', label: 'Reporting' },
  { id: 'notifications', label: 'Notifications' },
];

const StyleGuidePage = () => {
  const [activeTab, setActiveTab] = useState('headers');
  const { notify } = useNotifications();

  // === DEMO 1: List View Only ===
  const [d1Editing, setD1Editing] = useState(false);
  const [d1Selected, setD1Selected] = useState(null);
  const [d1Dirty, setD1Dirty] = useState(false);
  const [d1MenuOpen, setD1MenuOpen] = useState(false);
  const [d1Collapsed, setD1Collapsed] = useState(false);
  const [d1Search, setD1Search] = useState('');
  const d1MenuRef = useRef(null);

  // === DEMO 2: List + Datasheet ===
  const [d2View, setD2View] = useState('list');
  const [d2Editing, setD2Editing] = useState(false);
  const [d2Selected, setD2Selected] = useState(null);
  const [d2Dirty, setD2Dirty] = useState(false);
  const [d2MenuOpen, setD2MenuOpen] = useState(false);
  const [d2Collapsed, setD2Collapsed] = useState(false);
  const [d2Search, setD2Search] = useState('');
  const d2MenuRef = useRef(null);

  // === DEMO 3: Document/Reference ===
  const [d3MenuOpen, setD3MenuOpen] = useState(false);
  const d3MenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (d1MenuRef.current && !d1MenuRef.current.contains(e.target)) setD1MenuOpen(false);
      if (d2MenuRef.current && !d2MenuRef.current.contains(e.target)) setD2MenuOpen(false);
      if (d3MenuRef.current && !d3MenuRef.current.contains(e.target)) setD3MenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const demoItems = [
    { id: 1, name: 'Sample Record One', detail: 'First item', category: 'Type A' },
    { id: 2, name: 'Sample Record Two', detail: 'Second item', category: 'Type B' },
    { id: 3, name: 'Sample Record Three', detail: 'Third item', category: 'Type A' },
  ];

  const d1Filtered = demoItems.filter(i => !d1Search || i.name.toLowerCase().includes(d1Search.toLowerCase()));
  const d2Filtered = demoItems.filter(i => !d2Search || i.name.toLowerCase().includes(d2Search.toLowerCase()));

  return (
    <div className="dev-module">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <Palette size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Style Guide</h1>
          <span className="module-header-meta">UI patterns & components</span>
        </div>
        <div className="dev-module-header-right">
          <div className="action-menu" ref={useRef(null)}>
            <button className="action-menu-btn" onClick={() => {}} title="Actions"><GripVertical size={16} /></button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="related-tables-header" style={{ paddingLeft: 'var(--spacing-lg)', flexShrink: 0 }}>
        <div className="related-tables-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`related-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="dev-module-content-full" style={{ overflowY: 'auto', padding: 'var(--spacing-xl)' }}>

        {/* ============ HEADERS TAB ============ */}
        {activeTab === 'headers' && (
          <>
            <div className="sprint-display">
              <h2>Pattern 1: List View Only</h2>
              <p>Used for CRUD pages with a list/detail split panel. Examples: Users, Permissions List, Databases, Organizations.</p>
              <div className="sprint-spacer" />
              <table className="sprint-table">
                <thead><tr><th>Side</th><th>Elements (in order)</th></tr></thead>
                <tbody>
                  <tr><td><strong>Left</strong></td><td>Page Icon (primary color) → Title (h1) → Metadata (count/summary)</td></tr>
                  <tr><td><strong>Right</strong></td><td>+ / Trash toggle → Pencil / X toggle → Save → 6-dot action menu</td></tr>
                </tbody>
              </table>
              <div className="sprint-spacer" />
              <p><strong>6-dot menu:</strong> Refresh + page-specific actions (impersonate, export, etc.)</p>
              <div className="sprint-spacer" />
              <h3>Live Example</h3>
            </div>

            {/* LIVE DEMO 1 */}
            <div style={{ border: '2px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: '350px', display: 'flex', flexDirection: 'column', marginBottom: 'var(--spacing-xl)' }}>
              <div className="dev-module-header" style={{ flexShrink: 0 }}>
                <div className="dev-module-header-left">
                  <Palette size={20} style={{ color: 'var(--color-primary)' }} />
                  <h1>Sample Module</h1>
                  <span className="module-header-meta">{demoItems.length} records</span>
                </div>
                <div className="dev-module-header-right">
                  {d1Editing ? (
                    <button className="crud-btn" onClick={() => { if (window.confirm('Delete? (demo)')) { setD1Editing(false); setD1Selected(null); } else { setD1Editing(false); } }} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
                  ) : (
                    <button className="crud-btn" onClick={() => alert('Add new record dialog')} title="Add new"><Plus size={18} /></button>
                  )}
                  <button className={`crud-btn ${!d1Editing && !d1Selected ? 'disabled' : ''}`} onClick={() => { if (d1Editing) { setD1Editing(false); setD1Dirty(false); } else setD1Editing(true); }} disabled={!d1Selected && !d1Editing} title={d1Editing ? 'Cancel' : 'Edit'}>
                    {d1Editing ? <X size={18} /> : <Pencil size={18} />}
                  </button>
                  <button className={`crud-btn ${!d1Dirty ? 'disabled' : ''}`} onClick={() => { setD1Dirty(false); setD1Editing(false); }} disabled={!d1Dirty} title="Save"><Save size={18} /></button>
                  <div className="action-menu" ref={d1MenuRef}>
                    <button className="action-menu-btn" onClick={() => setD1MenuOpen(!d1MenuOpen)} title="Actions"><GripVertical size={16} /></button>
                    {d1MenuOpen && <div className="action-menu-dropdown"><button className="action-menu-item" onClick={() => setD1MenuOpen(false)}><RefreshCw size={16} /><span>Refresh</span></button></div>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {d1Collapsed ? (
                  <div className="list-panel-collapsed"><button onClick={() => setD1Collapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
                ) : (
                  <div className="list-panel">
                    <div className="list-panel-header">
                      <button onClick={() => setD1Collapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                        <input className="form-input" value={d1Search} onChange={e => setD1Search(e.target.value)} placeholder="Search..." style={{ paddingLeft: '28px', width: '100%' }} />
                      </div>
                    </div>
                    <div className="list-scroll">
                      {d1Filtered.map(item => (
                        <button key={item.id} onClick={() => { setD1Selected(item); setD1Editing(false); setD1Dirty(false); }} className={`list-item-btn ${d1Selected?.id === item.id ? 'selected' : ''}`}>
                          <span className="list-item-primary">{item.name}</span><span className="list-item-secondary">{item.detail}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="detail-panel">
                  {d1Selected ? (
                    <div className="detail-content" style={{ padding: 'var(--spacing-lg) var(--spacing-xl)' }}>
                      <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                        <div className="col-span-24"><div className="detail-section-header">Record Details</div></div>
                        <div className="form-group col-span-12"><label>Name</label><input className={`form-input${d1Dirty ? ' dirty' : ''}`} value={d1Selected.name} onChange={() => setD1Dirty(true)} readOnly={!d1Editing} /></div>
                        <div className="form-group col-span-12"><label>Description</label><input className={`form-input${d1Dirty ? ' dirty' : ''}`} value={d1Selected.detail} onChange={() => setD1Dirty(true)} readOnly={!d1Editing} /></div>
                      </div>
                    </div>
                  ) : (
                    <div className="dev-detail-empty"><Eye size={48} /><p>Select a record</p></div>
                  )}
                </div>
              </div>
            </div>

            <div className="sprint-display">
              <hr />
              <div className="sprint-spacer" />
              <h2>Pattern 2: List + Datasheet View</h2>
              <p>Used for pages that support both list/detail and grid editing. Examples: Permission Defaults, Dynamic Modules.</p>
              <div className="sprint-spacer" />
              <table className="sprint-table">
                <thead><tr><th>Side</th><th>Elements (in order)</th></tr></thead>
                <tbody>
                  <tr><td><strong>Left</strong></td><td>List icon → Table icon (tab-btn-icon, active highlight) → "X of Y records" (filtered count)</td></tr>
                  <tr><td><strong>Right</strong></td><td>+ / Trash toggle → Pencil / X toggle → Save → Search → 6-dot action menu</td></tr>
                </tbody>
              </table>
              <div className="sprint-spacer" />
              <p><strong>View-specific behavior:</strong></p>
              <table className="sprint-table">
                <thead><tr><th>View</th><th>CRUD</th><th>6-dot Contents</th></tr></thead>
                <tbody>
                  <tr><td><strong>List</strong></td><td>Standard CRUD (+ swaps to Trash in edit)</td><td>Refresh + page-specific</td></tr>
                  <tr><td><strong>Datasheet</strong></td><td>No Trash in header</td><td>Refresh + Download + Modify View + Bulk Delete</td></tr>
                </tbody>
              </table>
              <div className="sprint-spacer" />
              <p><strong>Datasheet grid features:</strong></p>
              <ul className="sprint-unordered-list">
                <li>Frozen/sticky header row</li>
                <li>Sortable columns (click header to cycle: none → asc → desc)</li>
                <li>Filterable columns (dropdown per column header)</li>
                <li>Resizable columns (drag column border)</li>
                <li>Column show/hide/reorder via Modify View in 6-dot</li>
                <li>Padding around grid (<code>.datasheet-view</code> wrapper)</li>
                <li>Download export + Bulk Delete via 6-dot</li>
              </ul>
              <div className="sprint-spacer" />
              <h3>Live Example</h3>
            </div>

            {/* LIVE DEMO 2 */}
            <div style={{ border: '2px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: '350px', display: 'flex', flexDirection: 'column', marginBottom: 'var(--spacing-xl)' }}>
              <div className="dev-module-header" style={{ flexShrink: 0 }}>
                <div className="dev-module-header-left">
                  <button className={`tab-btn-icon ${d2View === 'list' ? 'active' : ''}`} onClick={() => { setD2View('list'); setD2Editing(false); setD2Dirty(false); }} title="List view"><List size={18} /></button>
                  <button className={`tab-btn-icon ${d2View === 'datasheet' ? 'active' : ''}`} onClick={() => { setD2View('datasheet'); setD2Editing(false); setD2Dirty(false); setD2Selected(null); }} title="Datasheet view"><Table size={18} /></button>
                  <span className="module-header-meta">{d2Filtered.length} of {demoItems.length} records</span>
                </div>
                <div className="dev-module-header-right">
                  {d2View === 'list' ? (
                    d2Editing ? (
                      <button className="crud-btn" onClick={() => { if (window.confirm('Delete? (demo)')) { setD2Editing(false); setD2Selected(null); } else { setD2Editing(false); } }} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
                    ) : (
                      <button className="crud-btn" onClick={() => alert('Add new record dialog')} title="Add new"><Plus size={18} /></button>
                    )
                  ) : (
                    <button className="crud-btn" onClick={() => alert('Add new record dialog')} title="Add new"><Plus size={18} /></button>
                  )}
                  <button className={`crud-btn ${!d2Editing && !d2Selected ? 'disabled' : ''}`} onClick={() => { if (d2Editing) { setD2Editing(false); setD2Dirty(false); } else setD2Editing(true); }} disabled={d2View === 'list' ? (!d2Selected && !d2Editing) : false} title={d2Editing ? 'Cancel' : 'Edit'}>
                    {d2Editing ? <X size={18} /> : <Pencil size={18} />}
                  </button>
                  <button className={`crud-btn ${!d2Dirty ? 'disabled' : ''}`} onClick={() => { setD2Dirty(false); setD2Editing(false); }} disabled={!d2Dirty} title="Save"><Save size={18} /></button>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
                    <input className="form-input" value={d2Search} onChange={e => setD2Search(e.target.value)} placeholder="Search..." style={{ paddingLeft: '28px', width: '140px', height: '32px' }} />
                  </div>
                  <div className="action-menu" ref={d2MenuRef}>
                    <button className="action-menu-btn" onClick={() => setD2MenuOpen(!d2MenuOpen)} title="Actions"><GripVertical size={16} /></button>
                    {d2MenuOpen && (
                      <div className="action-menu-dropdown">
                        <button className="action-menu-item" onClick={() => setD2MenuOpen(false)}><RefreshCw size={16} /><span>Refresh</span></button>
                        {d2View === 'datasheet' && (
                          <>
                            <button className="action-menu-item" onClick={() => { setD2MenuOpen(false); alert('Download (demo)'); }}><Download size={16} /><span>Download</span></button>
                            <button className="action-menu-item" onClick={() => { setD2MenuOpen(false); alert('Modify View (demo)'); }}><SlidersHorizontal size={16} /><span>Modify View</span></button>
                            <button className="action-menu-item action-menu-item-danger" onClick={() => { setD2MenuOpen(false); alert('Bulk Delete (demo)'); }}><Trash2 size={16} /><span>Bulk Delete</span></button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {d2View === 'list' ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  {d2Collapsed ? (
                    <div className="list-panel-collapsed"><button onClick={() => setD2Collapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
                  ) : (
                    <div className="list-panel">
                      <div className="list-panel-header">
                        <button onClick={() => setD2Collapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                      </div>
                      <div className="list-scroll">
                        {d2Filtered.map(item => (
                          <button key={item.id} onClick={() => { setD2Selected(item); setD2Editing(false); setD2Dirty(false); }} className={`list-item-btn ${d2Selected?.id === item.id ? 'selected' : ''}`}>
                            <span className="list-item-primary">{item.name}</span><span className="list-item-secondary">{item.detail}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="detail-panel">
                    {d2Selected ? (
                      <div className="detail-content" style={{ padding: 'var(--spacing-lg) var(--spacing-xl)' }}>
                        <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                          <div className="col-span-24"><div className="detail-section-header">Record Details</div></div>
                          <div className="form-group col-span-8"><label>Name</label><input className={`form-input${d2Dirty ? ' dirty' : ''}`} value={d2Selected.name} onChange={() => setD2Dirty(true)} readOnly={!d2Editing} /></div>
                          <div className="form-group col-span-8"><label>Category</label><input className={`form-input${d2Dirty ? ' dirty' : ''}`} value={d2Selected.category} onChange={() => setD2Dirty(true)} readOnly={!d2Editing} /></div>
                          <div className="form-group col-span-8"><label>Description</label><input className={`form-input${d2Dirty ? ' dirty' : ''}`} value={d2Selected.detail} onChange={() => setD2Dirty(true)} readOnly={!d2Editing} /></div>
                        </div>
                      </div>
                    ) : (
                      <div className="dev-detail-empty"><Eye size={48} /><p>Select a record</p></div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="datasheet-view" style={{ backgroundColor: 'var(--color-bg-white)' }}>
                  <div className="data-grid-container">
                    <div className="data-grid-scroll-area">
                      <table className="data-grid">
                        <thead><tr><th>Name</th><th>Category</th><th>Description</th></tr></thead>
                        <tbody>
                          {d2Filtered.map(item => (
                            <tr key={item.id} className="clickable"><td style={{ fontWeight: 500 }}>{item.name}</td><td>{item.category}</td><td>{item.detail}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sprint-display">
              <hr />
              <div className="sprint-spacer" />
              <h2>Pattern 3: Document / Reference</h2>
              <p>Used for documentation, help pages, and reference content. Examples: Security Overview, Claude Notes, Style Guide.</p>
              <div className="sprint-spacer" />
              <table className="sprint-table">
                <thead><tr><th>Side</th><th>Elements (in order)</th></tr></thead>
                <tbody>
                  <tr><td><strong>Left</strong></td><td>Page Icon (primary color) → Title (h1) → Description or metrics</td></tr>
                  <tr><td><strong>Right</strong></td><td>[Search if applicable] → [Pencil / X → Save if editable] → 6-dot (Refresh + page-specific)</td></tr>
                </tbody>
              </table>
              <div className="sprint-spacer" />
              <p><strong>Optional features:</strong></p>
              <ul className="sprint-unordered-list">
                <li>Tab bar below header for sub-sections (this page uses tabs)</li>
                <li>Search — only if content is large enough to warrant it</li>
                <li>Edit mode — for editable docs (Pencil/X + Save, same CRUD standard)</li>
                <li>Full-width content area with <code>.sprint-display</code> for markdown-style rendering</li>
              </ul>
              <div className="sprint-spacer" />
              <h3>Live Example</h3>
            </div>

            {/* LIVE DEMO 3 */}
            <div style={{ border: '2px solid var(--color-gray-300)', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: '250px', display: 'flex', flexDirection: 'column' }}>
              <div className="dev-module-header" style={{ flexShrink: 0 }}>
                <div className="dev-module-header-left">
                  <Palette size={20} style={{ color: 'var(--color-primary)' }} />
                  <h1>Sample Documentation</h1>
                  <span className="module-header-meta">Reference page</span>
                </div>
                <div className="dev-module-header-right">
                  <div className="action-menu" ref={d3MenuRef}>
                    <button className="action-menu-btn" onClick={() => setD3MenuOpen(!d3MenuOpen)} title="Actions"><GripVertical size={16} /></button>
                    {d3MenuOpen && <div className="action-menu-dropdown"><button className="action-menu-item" onClick={() => setD3MenuOpen(false)}><RefreshCw size={16} /><span>Refresh</span></button></div>}
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-xl)', backgroundColor: 'var(--color-bg-white)' }}>
                <div className="sprint-display">
                  <h2>Section Title</h2>
                  <p>This is a read-only documentation page. Content rendered with <strong>sprint-display</strong> markdown styling.</p>
                  <ul className="sprint-unordered-list">
                    <li>Tables, lists, headings, code blocks all supported</li>
                    <li>Editable docs add Pencil/X + Save to the header</li>
                    <li>Tab bar can sit below the header for multi-section content</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============ CRUD & BUTTONS TAB ============ */}
        {activeTab === 'crud' && (
          <div className="sprint-display">
            <h2>CRUD Standard</h2>
            <p>Consistent button behavior anywhere CRUD operations exist — page headers, section headers, toolbars.</p>
            <div className="sprint-spacer" />
            <table className="sprint-table">
              <thead><tr><th>Button</th><th>Icon</th><th>View Mode</th><th>Edit Mode</th></tr></thead>
              <tbody>
                <tr><td><strong>Add / Delete</strong></td><td>+ / Trash (red)</td><td>+ opens new record dialog</td><td>Becomes red Trash — confirm to delete, cancel exits edit</td></tr>
                <tr><td><strong>Edit / Cancel</strong></td><td>Pencil / X</td><td>Pencil enters edit mode (requires selection)</td><td>Becomes X — cancels edit, discards dirty changes</td></tr>
                <tr><td><strong>Save</strong></td><td>Save</td><td>Disabled</td><td>Enabled only when dirty fields exist</td></tr>
              </tbody>
            </table>
            <div className="sprint-spacer" />
            <p><strong>State flow:</strong></p>
            <ol className="sprint-ordered-list">
              <li>Select a record in the list</li>
              <li>Click Pencil — fields become editable</li>
              <li>Change a value — field turns yellow (dirty), Save enables</li>
              <li>Save persists changes and exits edit mode</li>
              <li>X discards all dirty changes and exits edit mode</li>
              <li>Trash (red) confirms deletion — Yes deletes, No exits edit mode</li>
            </ol>
            <div className="sprint-spacer" />
            <p><strong>Rules:</strong></p>
            <ul className="sprint-unordered-list">
              <li>+ and Trash share the same button position — they swap based on edit state</li>
              <li>Trash is always red (<code>var(--color-error)</code>)</li>
              <li>Save is disabled unless there are dirty fields</li>
              <li>Pencil is disabled unless a record is selected</li>
              <li>Canceling a delete confirmation exits edit mode</li>
              <li>This pattern applies everywhere: page headers, section headers, inline toolbars</li>
            </ul>
            <div className="sprint-spacer" />
            <h2>6-Dot Action Menu</h2>
            <p>The <code>GripVertical</code> icon opens a dropdown with contextual actions. Always the rightmost element in the header.</p>
            <div className="sprint-spacer" />
            <table className="sprint-table">
              <thead><tr><th>Context</th><th>Contents</th></tr></thead>
              <tbody>
                <tr><td><strong>List View pages</strong></td><td>Refresh + page-specific actions (impersonate, export, etc.)</td></tr>
                <tr><td><strong>Datasheet mode</strong></td><td>Refresh + Download + Modify View + Bulk Delete (danger)</td></tr>
                <tr><td><strong>Doc/Reference pages</strong></td><td>Refresh + page-specific (print, etc.)</td></tr>
              </tbody>
            </table>
            <div className="sprint-spacer" />
            <h2>Button Styles</h2>
            <table className="sprint-table">
              <thead><tr><th>Class</th><th>Usage</th><th>Size</th></tr></thead>
              <tbody>
                <tr><td><code>crud-btn</code></td><td>Circular icon buttons in headers (32px)</td><td>Icon: 18px</td></tr>
                <tr><td><code>tab-btn-icon</code></td><td>View toggle buttons (list/table) with active highlight</td><td>Icon: 18px</td></tr>
                <tr><td><code>action-menu-btn</code></td><td>6-dot dropdown trigger (32px circle)</td><td>Icon: 16px</td></tr>
                <tr><td><code>btn btn-primary</code></td><td>Primary action button (modal footers, inline actions)</td><td>Text + icon</td></tr>
                <tr><td><code>btn btn-secondary</code></td><td>Secondary/cancel button</td><td>Text + icon</td></tr>
                <tr><td><code>btn-icon</code></td><td>Small bordered icon button for inline use</td><td>Icon only</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ============ DYNAMIC MODULE TAB ============ */}
        {activeTab === 'module' && (
          <>
            {/* Live labeled wireframe */}
            <div className="module-container" style={{ height: '500px' }}>
              {/* REGION A: MODULE HEADER */}
              <div className="dev-region dev-region-header">
                <div className="dev-region-label">A: MODULE HEADER (Pattern 2)</div>
                <div className="module-header">
                  <div className="module-header-left">
                    <div className="module-header-tabs">
                      <button className="tab-btn-icon active" title="List View"><List size={18} /></button>
                      <button className="tab-btn-icon" title="Datasheet View"><Table size={18} /></button>
                    </div>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>5 of 100 records</span>
                  </div>
                  <div className="module-header-actions">
                    <div className="module-header-crud">
                      <button className="crud-btn" title="Add new"><Plus size={18} /></button>
                      <button className="crud-btn disabled" disabled title="Edit"><Pencil size={18} /></button>
                      <button className="crud-btn disabled" disabled title="Save"><Save size={18} /></button>
                    </div>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
                      <input className="form-input" placeholder="Search..." readOnly style={{ paddingLeft: '28px', width: '160px', height: '32px' }} />
                    </div>
                    <div className="action-menu">
                      <button className="action-menu-btn" title="Actions"><GripVertical size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="list-view">
                {/* REGION B: LIST PANEL */}
                <div className="dev-region dev-region-list">
                  <div className="dev-region-label">B: LIST PANEL</div>
                  <div className="list-panel">
                    <div className="list-panel-content">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`list-item ${i === 1 ? 'selected' : ''}`}>
                          <div className="list-item-primary">Sample Record {i}</div>
                          <div className="list-item-secondary">Secondary info • metadata</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* REGION C: DETAIL PANEL */}
                <div className="dev-region dev-region-detail">
                  <div className="dev-region-label">C: DETAIL PANEL</div>
                  <div className="detail-panel" style={{ overflow: 'auto' }}>
                    {/* C1: Section Header */}
                    <div className="dev-subregion">
                      <div className="dev-subregion-label">C1: Section Header</div>
                      <div className="detail-section-header">General Information</div>
                    </div>

                    {/* C2: Form Grid */}
                    <div className="dev-subregion">
                      <div className="dev-subregion-label">C2: 24-Column Form Grid</div>
                      <div className="detail-grid-24">
                        <div className="form-group col-span-8">
                          <label>col-span-8 (third)</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-8">
                          <label>col-span-8</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-8">
                          <label>col-span-8</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-12">
                          <label>col-span-12 (half)</label>
                          <input type="text" value="Half width field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-12">
                          <label>col-span-12</label>
                          <input type="text" value="Half width field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>FK Field</label>
                          <button className="form-input fk-field"><span className="fk-field-value">Related Record →</span></button>
                        </div>
                        <div className="form-group col-span-6">
                          <label>Derived</label>
                          <input type="text" value="Computed" readOnly className="form-input derived-field" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>Dirty Field</label>
                          <input type="text" value="Changed!" readOnly className="form-input dirty" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>Checkbox</label>
                          <div className="checkbox-field" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <input type="checkbox" checked readOnly style={{ width: '1rem', height: '1rem' }} />
                            <span>Yes</span>
                          </div>
                        </div>
                        <div className="form-group col-span-24">
                          <label>col-span-24 (full width)</label>
                          <textarea value="Full width textarea field" readOnly className="form-input" rows={2} />
                        </div>
                      </div>
                    </div>

                    {/* D: Related Tables */}
                    <div className="dev-subregion" style={{ marginTop: 'var(--spacing-md)' }}>
                      <div className="dev-subregion-label">D: RELATED TABLES</div>
                      <div className="related-tables-section" style={{ height: '120px' }}>
                        <div className="detail-section-header related-section-header">
                          <span>Related Information</span>
                        </div>
                        <div className="related-tables-header">
                          <div className="related-tables-tabs">
                            <button className="related-tab active">Tab 1 <span className="related-tab-count">(3)</span></button>
                            <button className="related-tab">Tab 2</button>
                          </div>
                        </div>
                        <div style={{ padding: 'var(--spacing-sm)', color: 'var(--color-gray-400)', fontSize: 'var(--text-xs)' }}>FilterableDataGrid renders here</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sprint-display" style={{ marginTop: 'var(--spacing-xl)' }}>
              <h3>Region Reference</h3>
            <table className="sprint-table">
              <thead><tr><th>Region</th><th>CSS Class</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>A</strong></td><td><code>.module-header</code></td><td>Pattern 2 header — view toggle, record count, CRUD, search, 6-dot</td></tr>
                <tr><td><strong>B</strong></td><td><code>.list-panel</code></td><td>Collapsible list panel with search. Items show primary + secondary text.</td></tr>
                <tr><td><strong>C</strong></td><td><code>.detail-panel</code></td><td>Selected record detail with scrollable form content</td></tr>
                <tr><td><strong>C1</strong></td><td><code>.detail-section-header</code></td><td>Teal section bar separating field groups</td></tr>
                <tr><td><strong>C2</strong></td><td><code>.detail-grid-24</code></td><td>24-column CSS grid. Fields use <code>.form-group.col-span-N</code></td></tr>
                <tr><td><strong>D</strong></td><td><code>.related-tables-section</code></td><td>Related data below detail. Tabs + FilterableDataGrid. Resizable, collapsible.</td></tr>
                <tr><td><strong>E</strong></td><td><code>.datasheet-view</code></td><td>Full-width grid view with padding. Replaces B+C when datasheet is active.</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Header Behavior (Pattern 2)</h3>
            <table className="sprint-table">
              <thead><tr><th>View</th><th>Left Side</th><th>Right Side</th></tr></thead>
              <tbody>
                <tr><td><strong>List</strong></td><td>List icon (active) → Table icon → X of Y</td><td>+ / Trash → Pencil / X → Save → Search → 6-dot (Refresh)</td></tr>
                <tr><td><strong>Datasheet</strong></td><td>List icon → Table icon (active) → X of Y</td><td>+ → Pencil / X → Save → Search → 6-dot (Refresh + Download + Modify View + Bulk Delete)</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Detail Panel Form Fields</h3>
            <p>Fields are arranged in a 24-column grid. Common column spans:</p>
            <table className="sprint-table">
              <thead><tr><th>Span</th><th>Width</th><th>Typical Use</th></tr></thead>
              <tbody>
                <tr><td><code>col-span-4</code></td><td>~17%</td><td>Small fields (status, boolean, short code)</td></tr>
                <tr><td><code>col-span-6</code></td><td>25%</td><td>Quarter-width fields</td></tr>
                <tr><td><code>col-span-8</code></td><td>33%</td><td>Standard fields (name, email, date)</td></tr>
                <tr><td><code>col-span-12</code></td><td>50%</td><td>Half-width fields (description, address)</td></tr>
                <tr><td><code>col-span-24</code></td><td>100%</td><td>Full-width (textarea, section header, notes)</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Field Types</h3>
            <table className="sprint-table">
              <thead><tr><th>Type</th><th>Class / Behavior</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>Text</td><td><code>.form-input</code></td><td>Standard text input, 36px height</td></tr>
                <tr><td>Readonly</td><td><code>.form-input[readonly]</code></td><td>Light gray background, not editable</td></tr>
                <tr><td>Derived</td><td><code>.form-input.derived-field</code></td><td>Computed/immutable field (gray + italic)</td></tr>
                <tr><td>Dirty</td><td><code>.form-input.dirty</code></td><td>Yellow background + gold border when changed</td></tr>
                <tr><td>Select</td><td><code>select.form-input</code></td><td>Dropdown with cursor pointer</td></tr>
                <tr><td>Textarea</td><td><code>textarea.form-input</code></td><td>Multi-line, auto height</td></tr>
                <tr><td>FK Link</td><td><code>.form-input.fk-field</code></td><td>Clickable field that opens FK modal or navigates</td></tr>
                <tr><td>Checkbox</td><td><code>.checkbox-field</code></td><td>Checkbox + label in a flex row</td></tr>
                <tr><td>Section Header</td><td><code>.detail-section-header</code></td><td>Full-width teal bar, spans col-span-24</td></tr>
                <tr><td>Spacer</td><td><code>.col-span-N</code> (empty div)</td><td>Empty grid cell for layout control</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Related Tables</h3>
            <ul className="sprint-unordered-list">
              <li>Sits below the detail form in the detail panel</li>
              <li>Section header with collapse toggle and optional CRUD icons</li>
              <li>Tab bar for multiple related tables (e.g., Drawing Log, IP Addresses)</li>
              <li>Each tab loads a FilterableDataGrid with the related records</li>
              <li>Resizable via drag handle on the section header</li>
              <li>Double-click a row to open FK modal for that record</li>
            </ul>

            <div className="sprint-spacer" />

            <h3>Live Example</h3>
            <p>See the actual Dynamic Module in action by navigating to any data module (Buildings, Drawings, etc.).</p>
            </div>
          </>
        )}

        {/* ============ FORMS & LAYOUT TAB ============ */}
        {activeTab === 'forms' && (
          <div className="sprint-display">
            <h2>Forms & Layout</h2>
            <p>Coming soon — will document detail-grid-24, form-group, section headers, dirty tracking, edit/readonly modes.</p>
            <div className="sprint-spacer" />
            <h3>Grid System</h3>
            <ul className="sprint-unordered-list">
              <li><code>.detail-grid-24</code> — 24-column grid for form layouts</li>
              <li><code>.col-span-N</code> — column span classes (1-24)</li>
              <li><code>.form-group</code> — label + input wrapper with optional label positioning</li>
              <li><code>.detail-section-header</code> — teal section header bar</li>
            </ul>
            <div className="sprint-spacer" />
            <h3>Form Inputs</h3>
            <ul className="sprint-unordered-list">
              <li><code>.form-input</code> — standard input (36px height, gray border)</li>
              <li><code>.form-input.dirty</code> — yellow background + gold border for changed fields</li>
              <li><code>.form-input.derived-field</code> — gray background for readonly/computed fields</li>
              <li><code>.form-input[readonly]</code> — light gray background</li>
              <li><code>select.form-input</code> — dropdown with cursor pointer</li>
              <li><code>textarea.form-input</code> — auto-height multiline</li>
            </ul>
            <div className="sprint-spacer" />
            <h3>Related Tables</h3>
            <ul className="sprint-unordered-list">
              <li><code>.related-tables-section</code> — container for related data below detail form</li>
              <li><code>.detail-section-header.related-section-header</code> — section header with optional action buttons</li>
              <li><code>.related-tables-tabs</code> — tab bar for multiple related tables</li>
              <li>CRUD icons on section header (visible in edit mode only)</li>
            </ul>
          </div>
        )}

        {/* ============ DATA GRIDS TAB ============ */}
        {activeTab === 'grids' && (
          <div className="sprint-display">
            <h2>Data Grids</h2>
            <p>Coming soon — will document FilterableDataGrid, data-grid classes, datasheet editing patterns.</p>
            <div className="sprint-spacer" />
            <h3>Container Structure</h3>
            <ul className="sprint-unordered-list">
              <li><code>.datasheet-view</code> — outer wrapper with padding</li>
              <li><code>.data-grid-container</code> — flex container with border</li>
              <li><code>.data-grid-scroll-area</code> — scrollable area</li>
              <li><code>.data-grid</code> — the table element</li>
              <li><code>.data-grid thead</code> — sticky/frozen header (position: sticky, top: 0)</li>
            </ul>
            <div className="sprint-spacer" />
            <h3>Column Features</h3>
            <ul className="sprint-unordered-list">
              <li>Sort — click header to cycle: none → asc → desc</li>
              <li>Filter — dropdown per column with multi-select</li>
              <li>Resize — drag column border to resize</li>
              <li>Show/hide/reorder — via Modify View in 6-dot menu</li>
            </ul>
            <div className="sprint-spacer" />
            <h3>Row Interactions</h3>
            <ul className="sprint-unordered-list">
              <li><code>.clickable</code> — cursor pointer, hover highlight</li>
              <li>Row click — select record</li>
              <li>Row double-click — open edit modal (context-dependent)</li>
              <li>Alternating row colors via <code>:nth-child(even)</code></li>
              <li>Selected row highlight via inline style or class</li>
            </ul>
          </div>
        )}

        {/* ============ TEXT & COLORS TAB ============ */}
        {activeTab === 'colors' && (
          <div className="sprint-display">
            <h2>Text & Colors</h2>
            <p>Coming soon — will document CSS variables, fonts, status colors, spacing scale.</p>
            <div className="sprint-spacer" />
            <h3>Fonts</h3>
            <ul className="sprint-unordered-list">
              <li><code>--font-primary</code>: Orbitron, Rajdhani — titles, branding</li>
              <li><code>--font-secondary</code>: Rajdhani, Roboto Condensed — body, headers, grid headers</li>
            </ul>
            <div className="sprint-spacer" />
            <h3>Primary Colors</h3>
            <table className="sprint-table">
              <thead><tr><th>Variable</th><th>Value</th><th>Usage</th></tr></thead>
              <tbody>
                <tr><td><code>--color-primary</code></td><td>#1a5568</td><td>WM Teal — headers, active states, section bars</td></tr>
                <tr><td><code>--color-primary-dark</code></td><td>#0f3d4d</td><td>Hover states</td></tr>
                <tr><td><code>--color-primary-light</code></td><td>#e0f2f7</td><td>Selected row highlight, hover backgrounds</td></tr>
              </tbody>
            </table>
            <div className="sprint-spacer" />
            <h3>Status Colors</h3>
            <table className="sprint-table">
              <thead><tr><th>Variable</th><th>Value</th><th>Usage</th></tr></thead>
              <tbody>
                <tr><td><code>--color-success</code></td><td>#059669</td><td>Grant, Active, Complete</td></tr>
                <tr><td><code>--color-warning</code></td><td>#d97706</td><td>Overrides, In Progress</td></tr>
                <tr><td><code>--color-error</code></td><td>#dc2626</td><td>Deny, Trash/Delete, errors</td></tr>
              </tbody>
            </table>
            <div className="sprint-spacer" />
            <h3>Font Sizes</h3>
            <table className="sprint-table">
              <thead><tr><th>Variable</th><th>Value</th><th>Usage</th></tr></thead>
              <tbody>
                <tr><td><code>--text-xs</code></td><td>0.75rem</td><td>Grid headers, meta text, hints</td></tr>
                <tr><td><code>--text-sm</code></td><td>0.875rem</td><td>Form labels, list items, body text</td></tr>
                <tr><td><code>--text-base</code></td><td>1rem</td><td>Section headers, standard text</td></tr>
                <tr><td><code>--text-lg</code></td><td>1.125rem</td><td>Page titles in headers</td></tr>
              </tbody>
            </table>
            <div className="sprint-spacer" />
            <h3>Spacing Scale</h3>
            <table className="sprint-table">
              <thead><tr><th>Variable</th><th>Value</th></tr></thead>
              <tbody>
                <tr><td><code>--spacing-xs</code></td><td>0.25rem</td></tr>
                <tr><td><code>--spacing-sm</code></td><td>0.5rem</td></tr>
                <tr><td><code>--spacing-md</code></td><td>0.75rem</td></tr>
                <tr><td><code>--spacing-lg</code></td><td>1rem</td></tr>
                <tr><td><code>--spacing-xl</code></td><td>1.5rem</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ============ REPORTING TAB ============ */}
        {activeTab === 'reporting' && (
          <div className="sprint-display">
            <h2>Reporting Framework</h2>
            <p>Config-driven reporting engine — all report behavior defined in JSON, stored in the admin database. Use this reference as a template when building new reports.</p>

            {/* ---- GENERIC REPORT TEMPLATE MOCKUP ---- */}
            <div className="sprint-spacer" />
            <h3>Report Template Reference</h3>
            <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '12px' }}>Generic layout showing all report sections. Replace placeholder values with actual field names when creating a new report config.</p>

            {/* Branding Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Branding (PDF Header)</h4>
              <table className="sprint-table">
                <thead><tr><th>Field</th><th>Template Value</th><th>Notes</th></tr></thead>
                <tbody>
                  <tr><td><code>logo</code></td><td><code>null</code></td><td>Deferred — will be per-database. Set null for now.</td></tr>
                  <tr><td><code>companyName</code></td><td>"Company Name Here"</td><td>Default: "Wunderlich Malec Engineering"</td></tr>
                  <tr><td><code>companyAddress</code></td><td>"Address Line Here"</td><td>Default: "6101 Blue Circle Dr. Eden Prairie, MN 55343"</td></tr>
                  <tr><td><code>companyPhone</code></td><td>"Phone / Fax Here"</td><td>Default: "Tel: 952-933-3222 | Fax: 952-933-0608"</td></tr>
                  <tr><td><code>titleColor</code></td><td>"#1f497d"</td><td>Color used for report title in PDF output</td></tr>
                </tbody>
              </table>
            </div>

            {/* Layout Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Layout (PDF / Print)</h4>
              <table className="sprint-table">
                <thead><tr><th>Field</th><th>Template Value</th><th>Options</th></tr></thead>
                <tbody>
                  <tr><td><code>defaultOrientation</code></td><td>"landscape"</td><td><code>landscape</code> | <code>portrait</code></td></tr>
                  <tr><td><code>defaultPageSize</code></td><td>"tabloid"</td><td><code>letter</code> | <code>tabloid</code></td></tr>
                  <tr><td><code>allowedPageSizes</code></td><td>["letter", "tabloid"]</td><td>Array of sizes user can toggle between</td></tr>
                  <tr><td><code>showParameterSummary</code></td><td>true</td><td>Show selected filters at top of PDF</td></tr>
                  <tr><td><code>zebraStriping</code></td><td>true</td><td>Alternating row shading</td></tr>
                  <tr><td><code>fontSize</code></td><td>8</td><td>Data row font size (pt)</td></tr>
                  <tr><td><code>headerFontSize</code></td><td>10</td><td>Column header font size (pt)</td></tr>
                  <tr><td><code>fontFamily</code></td><td>"Calibri"</td><td>Font for PDF output</td></tr>
                </tbody>
              </table>
            </div>

            {/* Data Source Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Data Source</h4>
              <table className="sprint-table">
                <thead><tr><th>Field</th><th>Template Value</th><th>Notes</th></tr></thead>
                <tbody>
                  <tr><td><code>table</code></td><td>"Table_Name_Here"</td><td>Source table in the project database</td></tr>
                  <tr><td><code>baseFilter</code></td><td>"Status IN ('Active', 'Pending')"</td><td>Optional — SQL WHERE clause applied to all queries. Set <code>null</code> for no filter.</td></tr>
                </tbody>
              </table>
            </div>

            {/* Parameters Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Parameters (User Filters)</h4>
              <p style={{ color: '#666', fontSize: '0.8rem', margin: '0 0 8px 0' }}>Each parameter becomes a dropdown in the toolbar. Multiple selections within a parameter are OR'd. Parameters are AND'd together.</p>
              <table className="sprint-table">
                <thead><tr><th>#</th><th>field</th><th>label</th><th>operators</th><th>defaultOperator</th><th>lookupSource</th><th>lookupTable</th><th>lookupField</th><th>headerDisplayFormat</th></tr></thead>
                <tbody>
                  <tr>
                    <td>1</td><td><code>"Parameter_01_Here"</code></td><td>"Parameter 01 Here"</td>
                    <td>["contains", "startsWith", "endsWith", "equals"]</td>
                    <td>"contains"</td><td>"distinct"</td><td>"Table_Name_Here"</td><td>"Parameter_01_Here"</td>
                    <td><code>null</code> or <code>"[Field_A] + ' - ' + [Field_B]"</code></td>
                  </tr>
                  <tr>
                    <td>2</td><td><code>"Parameter_02_Here"</code></td><td>"Parameter 02 Here"</td>
                    <td>["contains", "startsWith", "endsWith", "equals"]</td>
                    <td>"contains"</td><td>"distinct"</td><td>"Table_Name_Here"</td><td>"Parameter_02_Here"</td>
                    <td><code>null</code></td>
                  </tr>
                  <tr>
                    <td>3</td><td><code>"Parameter_03_Here"</code></td><td>"Parameter 03 Here"</td>
                    <td>["contains", "startsWith", "endsWith", "equals"]</td>
                    <td>"contains"</td><td>"distinct"</td><td>"Table_Name_Here"</td><td>"Parameter_03_Here"</td>
                    <td><code>null</code></td>
                  </tr>
                  <tr>
                    <td>4</td><td><code>"Parameter_04_Here"</code></td><td>"Parameter 04 Here"</td>
                    <td>["contains", "startsWith", "endsWith", "equals"]</td>
                    <td>"contains"</td><td>"distinct"</td><td>"Table_Name_Here"</td><td>"Parameter_04_Here"</td>
                    <td><code>null</code></td>
                  </tr>
                </tbody>
              </table>
              <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '6px' }}>
                <strong>headerDisplayFormat</strong>: For FK parameters — controls what the dropdown shows. Uses FK table column references in brackets. <code>null</code> = uses FK_Display_Field from AppCfg_FieldMetadata.
              </p>
            </div>

            {/* Columns Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Columns (Report Output)</h4>
              <table className="sprint-table">
                <thead><tr><th>order</th><th>field</th><th>label</th><th>width</th><th>align</th><th>fk</th></tr></thead>
                <tbody>
                  <tr><td>1</td><td><code>"Column_01_Here"</code></td><td>"Column 01 Here"</td><td>10</td><td>"left"</td><td>false</td></tr>
                  <tr><td>2</td><td><code>"Column_02_Here"</code></td><td>"Column 02 Here"</td><td>16</td><td>"left"</td><td>false</td></tr>
                  <tr><td>3</td><td><code>"Column_03_Here"</code></td><td>"Column 03 Here"</td><td>8</td><td>"left"</td><td>true</td></tr>
                  <tr><td>4</td><td><code>"Column_04_Here"</code></td><td>"Column 04 Here"</td><td>6</td><td>"center"</td><td>false</td></tr>
                  <tr><td>5</td><td><code>"Column_05_Here"</code></td><td>"Column 05 Here"</td><td>5</td><td>"center"</td><td>true</td></tr>
                  <tr><td>6</td><td><code>"Column_06_Here"</code></td><td>"Column 06 Here"</td><td>4</td><td>"center"</td><td>false</td></tr>
                </tbody>
              </table>
              <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '6px' }}>
                <strong>width</strong>: Relative % of total report width. <strong>fk</strong>: If true, backend joins to FK table and shows display value. <strong>align</strong>: left | center | right.
              </p>
            </div>

            {/* Sorting Section */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Sorting (Default Order)</h4>
              <table className="sprint-table">
                <thead><tr><th>#</th><th>field</th><th>direction</th></tr></thead>
                <tbody>
                  <tr><td>1</td><td><code>"Sort_Field_01_Here"</code></td><td>"asc"</td></tr>
                  <tr><td>2</td><td><code>"Sort_Field_02_Here"</code></td><td>"asc"</td></tr>
                </tbody>
              </table>
              <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '6px' }}>
                Applied as default sort. Users can override with column header clicks (Shift+Click for multi-column).
              </p>
            </div>

            {/* Misc Config */}
            <div style={{ border: '1px solid #ccc', borderRadius: '6px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#1f497d' }}>Other Config Sections</h4>
              <table className="sprint-table">
                <thead><tr><th>Section</th><th>Template Value</th><th>Notes</th></tr></thead>
                <tbody>
                  <tr><td><code>reportId</code></td><td><code>"your-report-id"</code></td><td>Must match <code>Table_Name</code> in AppCfg_DynamicModules. Used in URL slug.</td></tr>
                  <tr><td><code>reportName</code></td><td><code>"Your Report Name"</code></td><td>Display name shown in report header and PDF title</td></tr>
                  <tr><td><code>description</code></td><td><code>"Brief description of what this report shows"</code></td><td>Shown in report viewer subtitle</td></tr>
                  <tr><td><code>requiredRole</code></td><td><code>null</code></td><td>Future — RBAC role required to access this report</td></tr>
                  <tr><td><code>conditionalFormatting</code></td><td><code>[]</code></td><td>Future — row/cell highlighting rules</td></tr>
                  <tr><td><code>logging.enabled</code></td><td><code>false</code></td><td>Enable query logging for debugging</td></tr>
                </tbody>
              </table>
            </div>

            {/* ---- HOW TO ADD A NEW REPORT ---- */}
            <div className="sprint-spacer" />
            <h3>How to Add a New Report</h3>
            <ol style={{ lineHeight: '1.8', paddingLeft: '20px' }}>
              <li>Create the JSON config using the template sections above</li>
              <li>Insert into <code>AppCfg_DynamicModules</code>: <code>Table_Name = 'your-report-id'</code>, <code>Config_Type = 'report'</code>, <code>Config = your JSON</code></li>
              <li>Add entry to <code>reportRegistry.js</code>: map URL slug to config ID</li>
              <li>Add entry to <code>navigationConfig.js</code>: under Reports section, set <code>future: false</code></li>
              <li>No backend code changes needed — ReportController is fully generic</li>
            </ol>

            {/* ---- ARCHITECTURE & REFERENCE ---- */}
            <div className="sprint-spacer" />
            <h3>Architecture</h3>
            <table className="sprint-table">
              <thead><tr><th>Layer</th><th>Component</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr><td>Config Storage</td><td><code>AppCfg_DynamicModules</code></td><td>Admin DB table, <code>Config_Type='report'</code>. Composite PK: Table_Name + Config_Type.</td></tr>
                <tr><td>Backend</td><td><code>ReportController.cs</code></td><td>Loads config from DB, builds dynamic SQL with FK joins, executes parameterized queries.</td></tr>
                <tr><td>Frontend</td><td><code>ReportViewerPage.js</code></td><td>Generic report viewer at <code>/reports/:reportSlug</code>. Reads config, renders parameters + results.</td></tr>
                <tr><td>Registry</td><td><code>reportRegistry.js</code></td><td>Maps URL slugs to report config IDs.</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />
            <h3>API Endpoints</h3>
            <table className="sprint-table">
              <thead><tr><th>Method</th><th>Endpoint</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr><td><code>GET</code></td><td><code>/api/report/configs</code></td><td>List all available report config IDs</td></tr>
                <tr><td><code>GET</code></td><td><code>/api/report/{'{reportId}'}/config</code></td><td>Get report config enriched with Field Manager column widths</td></tr>
                <tr><td><code>POST</code></td><td><code>/api/report/{'{reportId}'}/run</code></td><td>Execute report — returns filtered, sorted data with FK display values</td></tr>
                <tr><td><code>GET</code></td><td><code>/api/report/{'{reportId}'}/parameter-options/{'{field}'}</code></td><td>Cascading dropdown options for parameter fields</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />
            <h3>Frontend Components</h3>
            <table className="sprint-table">
              <thead><tr><th>Component</th><th>Purpose</th></tr></thead>
              <tbody>
                <tr><td><code>ReportViewerPage.js</code></td><td>Main page — loads config, manages parameter state, displays results table</td></tr>
                <tr><td><code>ReportParameterCombobox.js</code></td><td>Multi-select dropdown with cascading filters between parameters</td></tr>
                <tr><td><code>ReportToolbar.js</code></td><td>Run Report, Clear, Sort, Export controls</td></tr>
                <tr><td><code>ReportPaginator.js</code></td><td>Page navigation for result set</td></tr>
                <tr><td><code>ReportPdfDocument.js</code></td><td>PDF document structure via @react-pdf</td></tr>
                <tr><td><code>ReportPdfPage.js</code></td><td>PDF page layout with headers, branding, data rows</td></tr>
                <tr><td><code>reportExcelExporter.js</code></td><td>Excel export via ExcelJS with WM branding theme</td></tr>
                <tr><td><code>reportCsvExporter.js</code></td><td>CSV export (RFC 4180 with BOM)</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />
            <h3>Key Design Decisions</h3>
            <ul className="sprint-unordered-list">
              <li><strong>No SQL Views</strong> — ReportController reuses DynamicController's FK join pattern via <code>AppCfg_FieldMetadata</code>. Zero Azure DDL needed per report.</li>
              <li><strong>Config in DB</strong> — Report configs stored in <code>AppCfg_DynamicModules</code> (same pattern as standard-view module configs).</li>
              <li><strong>Cascading Parameters</strong> — Selecting a value in one parameter narrows the options in other parameters. Multi-select with OR within, AND across.</li>
              <li><strong>FK Display Values</strong> — FK columns show display text (e.g., Source shows Project_Number + Project_Desc) but filter by ID for equals, display text for contains/startsWith/endsWith.</li>
              <li><strong>Client-Side Sorting</strong> — Multi-column sort via Shift+Click. Server returns unsorted (or config-default sorted) data.</li>
              <li><strong>Adding a New Report</strong> — Insert a row into <code>AppCfg_DynamicModules</code> with <code>Config_Type='report'</code>, add to <code>reportRegistry.js</code>, add to <code>navigationConfig.js</code>. No backend code changes needed.</li>
            </ul>
          </div>
        )}

        {/* ============ NOTIFICATIONS TAB ============ */}
        {activeTab === 'notifications' && (
          <div className="sprint-display">
            <h2>Notification System</h2>
            <p>Two channels: <strong>Header alerts</strong> (top center) for things needing attention, <strong>Footer status</strong> (bottom left) for transient acknowledgements.</p>

            <div className="sprint-spacer" />

            <table className="sprint-table">
              <thead><tr><th>Call</th><th>Channel</th><th>Auto-dismiss</th><th>Use for</th></tr></thead>
              <tbody>
                <tr><td><code>notify.error(msg)</code></td><td>Header</td><td>Never — requires X</td><td>API failures, blocking errors</td></tr>
                <tr><td><code>notify.warning(msg)</code></td><td>Header</td><td>5 seconds</td><td>Non-fatal issues, heads-up messages</td></tr>
                <tr><td><code>notify.confirm(msg, onConfirm)</code></td><td>Header</td><td>Never — Yes/No buttons</td><td>Destructive action confirmations</td></tr>
                <tr><td><code>notify.success(msg)</code></td><td>Footer</td><td>3 seconds</td><td>Save confirmations, completed actions</td></tr>
                <tr><td><code>notify.info(msg)</code></td><td>Footer</td><td>4 seconds</td><td>Background completions, quiet FYIs</td></tr>
                <tr><td><code>notify.status(msg, 0)</code></td><td>Footer</td><td>Never (persistent)</td><td>Loading / working states</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />
            <h3>Multi-Alert Behavior</h3>
            <p>1 alert renders normally. 2+ alerts collapse to a summary row (expanded by default) showing the worst severity icon, alert count, a Dismiss All button, and a collapse toggle. Each alert in the expanded stack retains its individual dismiss control.</p>

            <div className="sprint-spacer" />
            <h3>Live Examples</h3>
            <p>Fire these to see them in context. Stack multiples to see the collapse behavior.</p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
              {[
                { label: 'Error', color: 'var(--color-error)', action: () => notify.error('Something went wrong — this requires acknowledgement.') },
                { label: 'Warning', color: 'var(--color-status-warning)', action: () => notify.warning('Heads up — this will auto-dismiss in 5 seconds.') },
                { label: 'Confirm', color: 'var(--color-primary)', action: () => notify.confirm('Delete this record? This cannot be undone.', () => notify.success('Record deleted.'), { confirmText: 'Delete', cancelText: 'Cancel' }) },
                { label: 'Success (footer)', color: 'var(--color-status-success)', action: () => notify.success('Record saved successfully.') },
                { label: 'Info (footer)', color: 'var(--color-status-info)', action: () => notify.info('Background sync completed — 142 records updated.') },
                { label: 'Persistent Status', color: 'var(--color-text-secondary)', action: () => notify.status('Syncing data…', 0) },
                { label: 'Clear Status', color: 'var(--color-text-secondary)', action: () => notify.clearStatus() },
              ].map(({ label, color, action }) => (
                <button key={label} onClick={action} style={{
                  padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 500,
                  border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
                  background: 'transparent', color, cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'opacity 0.15s ease'
                }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="sprint-spacer" />
            <h3>Stack Test</h3>
            <p>Fires N alerts at once — watch the first-to-stack transition and scrolling behavior.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
              {[3, 5, 10, 20].map(n => (
                <button key={n} onClick={() => {
                  const types = ['error', 'warning', 'error', 'warning', 'error'];
                  const msgs = [
                    'Database connection timed out.',
                    'Some fields failed validation.',
                    'Save failed — record is locked by another user.',
                    'Session will expire in 5 minutes.',
                    'Sync conflict detected on Tags table.',
                    'Permission denied on Buildings endpoint.',
                    'FK reference missing — check Organization field.',
                    'API rate limit approaching.',
                    'Draft changes not saved.',
                    'Network timeout on report export.',
                    'Schema mismatch detected.',
                    'User impersonation session active.',
                    'Stale data warning — last refresh over 10 minutes ago.',
                    'Export queue is full.',
                    'Duplicate record detected.',
                    'Backend returned 503.',
                    'Token refresh failed.',
                    'File size exceeds limit.',
                    'Required field missing.',
                    'Batch operation partially failed.',
                  ];
                  for (let i = 0; i < n; i++) {
                    const type = types[i % types.length];
                    if (type === 'error') notify.error(msgs[i % msgs.length]);
                    else notify.warning(msgs[i % msgs.length]);
                  }
                }} style={{
                  padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 500,
                  border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap'
                }}>
                  Stack {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleGuidePage;
